import ky from "ky";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { useState } from "react";
import { useForm } from "react-hook-form";

import { analyticsEvent } from "@up/analytics";
import { ProfileType, STRICT_US_STATES, logError, useTheme } from "@up/data";
import { LoadingModal, Toast, UPLink } from "@up/ui";

import { Age, Email, FirstName, LastName, Type } from "../form-fields";
import GenericError from "../GenericError";
import { Legal } from "../Legal";
import { LEAD_CAPTURE_UPCOMING_COURSE_V2_MODAL_ID } from "../modal-ids";

import type { UserType } from "../form-fields";
import type { GeoData } from "@up/data";

type SaveState = "idle" | "loading" | "error" | "success";
type FormBoolean = "true" | "false";

const BASE_URL = process.env["NEXT_PUBLIC_HALL_OF_RECORDS_URI"] as string;

const DEFAULT_VALUES: {
  email: string;
  type: UserType;
  formId: string;

  age?: string;
  crossPartnerMarketingOptIn?: FormBoolean;
  guardianEmail?: string;
  state?: string;
  underageConsent?: FormBoolean;
} = {
  formId: LEAD_CAPTURE_UPCOMING_COURSE_V2_MODAL_ID,
  email: "",
  type: "",
};

export const Form = function ({
  close,
  geoData,
  onSuccess,
  trigger = "unknown",
}: {
  close: () => void;
  onSuccess: () => void;
  trigger?: string;
  geoData: GeoData;
}) {
  const theme = useTheme();
  const reducedMotion = useReducedMotion();
  const [saveStatus, setSaveStatus] = useState<SaveState>("idle");

  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
    watch,
  } = useForm({
    defaultValues: DEFAULT_VALUES,
    shouldUnregister: true,
  });

  const formValues = watch();

  const onSubmit = handleSubmit(async (data: typeof DEFAULT_VALUES) => {
    setSaveStatus("loading");

    if (!data) {
      return;
    }

    const element = document.getElementById("catalogueid");
    const id = element?.getAttribute("content") ?? null;

    const payload = {
      ...data,
      crossPartnerMarketingOptIn: data.crossPartnerMarketingOptIn === "true",
      catalogueId: id,
      formId: LEAD_CAPTURE_UPCOMING_COURSE_V2_MODAL_ID,
      geoData: geoData,
      location: JSON.parse(JSON.stringify(window.location)),
      touppAccept: true,
    };

    try {
      await ky.post(BASE_URL + `/v2/c/lc/${theme.partnerID}`, { json: payload });
      setSaveStatus("success");
      onSuccess();
    } catch (err) {
      logError({ action: "LEAD_CAPTURE_SUBMIT", error: err });
      setSaveStatus("error");
    }
  });

  const TYPE_SELECTED = formValues["type"] !== "";
  const IS_STUDENT = formValues["type"] === ProfileType.Student;
  const IS_STRICT_US_STATE =
    geoData.isStrictUSState ||
    STRICT_US_STATES.includes(formValues.state ?? "");

  if (saveStatus === "success") {
    return (
      <div className="flex flex-col gap-4">
        <div className="pt-4">
          <Toast variant="success">
            Thank you for your interest. We will be in touch shortly.
          </Toast>
        </div>
        <UPLink
          className={theme.buttonPrimaryClass}
          href="/syllabus"
          onClick={() => {
            analyticsEvent({
              event: "downloadSleekNote",
            });
            close();
          }}
        >
          Close
        </UPLink>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-4">
      {saveStatus === "loading" ? <LoadingModal /> : null}

      <p className="mb-4">
        Share your information below so we can notify you when the application
        period is open.
      </p>

      <Type
        control={control}
        formErrors={errors}
        formValues={formValues}
        register={register}
      />

      <AnimatePresence>
        {TYPE_SELECTED ? (
          <motion.div
            className="flex flex-col gap-4"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{
              ease: [0.87, 0, 0.13, 1],
              duration: reducedMotion ? 0 : 0.75,
            }}
          >
            <hr />

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <FirstName
                formErrors={errors}
                formValues={formValues}
                register={register}
              />

              <LastName
                formErrors={errors}
                formValues={formValues}
                register={register}
              />
            </div>

            {IS_STUDENT ? (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
                <div className="col-span-3">
                  <Email
                    formErrors={errors}
                    formValues={formValues}
                    register={register}
                  />
                </div>
                <div className="col-span-1">
                  <div className="max-w-[10rem]">
                    <Age
                      control={control}
                      formErrors={errors}
                      formValues={formValues}
                      register={register}
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div>
                <Email
                  formErrors={errors}
                  formValues={formValues}
                  register={register}
                />
              </div>
            )}

            <hr />

            <Legal
              age={formValues.age ?? ""}
              isDomestic={geoData.isDomestic}
              isStrictUSState={IS_STRICT_US_STATE}
              isStudent={IS_STUDENT}
              register={register}
            />
          </motion.div>
        ) : null}
      </AnimatePresence>

      {saveStatus === "error" ? <GenericError /> : null}

      <button
        className={`${theme.buttonPrimaryClass} w-full ${
          saveStatus === "loading" ? "cursor-wait opacity-50" : ""
        }`}
        disabled={saveStatus === "loading"}
        type="submit"
      >
        Submit
      </button>
    </form>
  );
};
